// Maps each form-field component in InsuranceFormFields.tsx to the
// PolicyFundingSource fields it edits. The Policy Details card uses this
// to render exactly the inputs the form would show for a given policy
// type — see usage in `buildPolicyDetails.ts`.

import { type FC } from 'react';
import { FORM_FIELD_COMPONENTS } from '@/components/InsuranceFormFields';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { FundingPolicyType } from '@shared/appModel';
import { isMembershipProgram } from '@shared/FundingPolicyTypeDef';
import { FormatKind } from './types';

export interface DescriptorContext {
  policy: PolicyFundingSource;
  clientHasStartedLtc: boolean;
}

export type DescriptorText = string | ((context: DescriptorContext) => string | undefined);

export interface PolicyFieldDescriptor {
  label: DescriptorText;
  key?: keyof PolicyFundingSource;
  format: FormatKind;
  tooltip?: DescriptorText;
  valueMultiplier?: number;
  getValue?: (context: DescriptorContext) => unknown;
}

export type FormFieldGroup =
  | { kind: 'fields'; fields: PolicyFieldDescriptor[] }
  | { kind: 'perEnv' };

function readQuotePremiumFrequency(
  sourceQuote: PolicyFundingSource['sourceQuote'],
): 'Monthly' | 'Annually' | undefined {
  if (!sourceQuote) return undefined;
  let quote: unknown = sourceQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return undefined;
    }
  }
  if (quote && typeof quote === 'object' && 'premiumFrequency' in quote) {
    const value = (quote as { premiumFrequency?: unknown }).premiumFrequency;
    if (value === 'Monthly' || value === 'Annually') return value;
  }
  return undefined;
}

function isAnnualPremiumDisplay(policy: PolicyFundingSource): boolean {
  if (isMembershipProgram(policy.policyType)) return false;
  return (
    readQuotePremiumFrequency(policy.sourceQuote) === 'Annually' ||
    (policy.policyLimitedPayYears ?? 0) > 0
  );
}

function premiumCostLabel({ policy }: DescriptorContext): string {
  if (isMembershipProgram(policy.policyType)) return 'Monthly maintenance fees';
  return isAnnualPremiumDisplay(policy) ? 'Annual premium' : 'Monthly premium';
}

function premiumCostValue({ policy }: DescriptorContext): unknown {
  const monthlyCost = policy.policyPremiumMonthlyCost;
  if (typeof monthlyCost !== 'number') return monthlyCost;
  return isAnnualPremiumDisplay(policy) ? monthlyCost * 12 : monthlyCost;
}

function indexedAnnuityCareMonthlyLimit({ policy }: DescriptorContext): number | null {
  const dailyLimits = [
    policy.dailyHomeBenefitAmount,
    policy.dailyIndependentLivingBenefitAmount,
    policy.dailyAssistedLivingBenefitAmount,
    policy.dailyNursingHomeBenefitAmount,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (dailyLimits.length === 0) return null;
  return Math.max(...dailyLimits) * DAYS_IN_MONTH;
}

// One form component may render multiple FieldContainers; the descriptor
// is a flat list of fields with no per-component title (the card renders
// a single flat grid).
let componentDescriptors: Map<FC, FormFieldGroup[]> | null = null;

function getComponentDescriptors(): Map<FC, FormFieldGroup[]> {
  if (componentDescriptors) return componentDescriptors;

  const C = FORM_FIELD_COMPONENTS;
  componentDescriptors = new Map<FC, FormFieldGroup[]>([
    [
      C.WaitingPeriodDaysFieldGroup,
      [
        {
          kind: 'fields',
          fields: [
            { label: 'Home care WP', key: 'homeCareWaitingPeriodDays', format: 'days' },
            { label: 'Facility care WP', key: 'facilityCareWaitingPeriodDays', format: 'days' },
            {
              label: 'Waiting period reimbursement',
              key: 'hasRetroactiveWaitingPeriodReimbursement',
              format: 'text',
              getValue: ({ policy }) =>
                policy.hasRetroactiveWaitingPeriodReimbursement ? 'Reimbursed' : 'Not reimbursed',
            },
          ],
        },
      ],
    ],
    [
      C.FaceAmountField,
      [
        {
          kind: 'fields',
          fields: [{ label: 'Face amount', key: 'policyMaximumBenefitAmount', format: 'currency' }],
        },
      ],
    ],
    [
      C.FaceAmountAtAgeField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Face amount at LTC start',
              key: 'policyMaximumBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.MaximumBenefitAmountField,
      [
        {
          kind: 'fields',
          fields: [{ label: 'Max benefit', key: 'policyMaximumBenefitAmount', format: 'currency' }],
        },
      ],
    ],
    [
      C.TotalLTCBalanceAtAgeField,
      [
        {
          kind: 'fields',
          fields: [
            {
              // The source title includes ltcAtAge from Redux; the details
              // model is policy-only, so use the stable LTC-start wording.
              label: 'Total LTC balance at LTC start',
              key: 'policyMaximumBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.JointTotalLTCBalanceAtAgeField,
      [
        {
          kind: 'fields',
          fields: [
            {
              // The source title includes the policyholder's projected age and
              // partner names from Redux, which are not available here.
              label: 'Total LTC balance at policy activation age',
              key: 'policyMaximumBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.JointDeathBenefitAtAgeField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Face amount at policy activation age',
              key: 'policyMaximumBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.MinimumGuaranteedBenefitAmountField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Min guaranteed DB',
              key: 'policyMinimumGuaranteedBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.ContinuationOfBenefitsField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: ({ policy }) =>
                policy.policyType === FundingPolicyType.nylAssetFlex ? 'EOB amount' : 'COB amount',
              key: 'policyContinuationBenefitAmount',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [
      C.IndexedAnnuityCareGuaranteedValuesFields,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Guaranteed LTC benefit balance',
              key: 'policyBaseBenefitAmountAtIssue',
              format: 'currency',
              getValue: ({ policy }) =>
                policy.policyBaseBenefitAmountAtIssue ?? policy.policyMaximumBenefitAmount ?? null,
            },
            {
              label: 'LTC monthly withdrawal limit',
              key: 'dailyHomeBenefitAmount',
              format: 'currency',
              getValue: indexedAnnuityCareMonthlyLimit,
            },
            {
              label: 'Base benefit period',
              key: 'policyAccelerationBenefitDurationMonths',
              format: 'months',
            },
            {
              label: 'COB rider LTC balance',
              key: 'policyContinuationBenefitAmountAtIssue',
              format: 'currency',
              getValue: ({ policy }) =>
                policy.policyContinuationBenefitAmountAtIssue ??
                policy.policyContinuationBenefitAmount ??
                null,
            },
            {
              label: 'COB benefit period',
              key: 'policyEobBenefitDurationMonths',
              format: 'months',
            },
            {
              label: 'COB inflation rate',
              key: 'policyInflationProtectionOnCOBAmount',
              format: 'percent',
            },
            {
              label: 'COB inflation years',
              key: 'policyInflationProtectionYears',
              format: 'years',
            },
          ],
        },
      ],
    ],
    [
      C.ForecareGuaranteedRateModelFields,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Accelerated / contract value',
              key: 'policyBaseBenefitAmountAtIssue',
              format: 'currency',
            },
            {
              label: 'Maximum extended benefit',
              key: 'policyContinuationBenefitAmountAtIssue',
              format: 'currency',
            },
            {
              label: 'Contract value / death benefit',
              key: 'policyAccumulatedValueAtIssue',
              format: 'currency',
            },
            {
              label: 'Initial guaranteed rate',
              key: 'policyBaseBenefitGuaranteedInitialRate',
              format: 'percent',
            },
            {
              label: 'Initial rate through year',
              key: 'policyBaseBenefitGuaranteedInitialRateYears',
              format: 'years',
            },
            {
              label: 'Renewal guaranteed rate',
              key: 'policyBaseBenefitGuaranteedRenewalRate',
              format: 'percent',
            },
            {
              label: 'LTC rider monthly charge',
              key: 'policyMonthlyContinuationBenefitChargeRate',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.AccelerationBenefitDurationMonthsField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Acceleration duration',
              key: 'policyAccelerationBenefitDurationMonths',
              format: 'months',
            },
          ],
        },
      ],
    ],
    [
      C.EobBenefitDurationMonthsField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'EOB duration',
              key: 'policyEobBenefitDurationMonths',
              format: 'months',
            },
          ],
        },
      ],
    ],
    [
      C.BenefitPeriodMonthsField,
      [
        {
          kind: 'fields',
          fields: [{ label: 'Benefit period', key: 'policyBenefitPeriodMonths', format: 'months' }],
        },
      ],
    ],
    [
      C.PremiumStartYearField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: ({ policy }) =>
                isMembershipProgram(policy.policyType)
                  ? 'Membership start year'
                  : 'Policy purchase year',
              key: 'policyPremiumStartYear',
              format: 'year',
            },
          ],
        },
      ],
    ],
    [
      C.PremiumMonthlyCostField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: premiumCostLabel,
              key: 'policyPremiumMonthlyCost',
              format: 'currency',
              getValue: premiumCostValue,
            },
          ],
        },
      ],
    ],
    [
      C.PremiumIncreaseRateField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: ({ policy }) =>
                isMembershipProgram(policy.policyType)
                  ? 'Maintenance fee increase rate'
                  : 'Premium increase rate',
              key: 'policyPremiumIncreaseRate',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.PremiumIncreaseYearsField,
      [
        {
          kind: 'fields',
          fields: [
            { label: 'Premium increase years', key: 'policyPremiumIncreaseYears', format: 'years' },
          ],
        },
      ],
    ],
    [
      C.LimitedPayYearsField,
      [
        {
          kind: 'fields',
          fields: [{ label: 'Limited-pay years', key: 'policyLimitedPayYears', format: 'years' }],
        },
      ],
    ],
    [
      C.LumpSumPaymentField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: ({ policy }) =>
                isMembershipProgram(policy.policyType) ? 'Entrance fee' : 'Lump-sum payment',
              key: 'policyLumpSumPayment',
              format: 'currency',
            },
          ],
        },
      ],
    ],
    [C.DailyMaxBenefitsFieldGroup, [{ kind: 'perEnv' }]],
    [
      C.InflationProtectionField,
      [
        {
          kind: 'fields',
          fields: [
            { label: 'Inflation protection', key: 'policyInflationProtection', format: 'percent' },
          ],
        },
      ],
    ],
    [
      C.InflationProtectionOnFaceAmountField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Inflation on face',
              key: 'policyInflationProtectionOnFaceAmount',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.InflationProtectionOnCOBAmountField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Inflation on COB',
              key: 'policyInflationProtectionOnCOBAmount',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.IsSimpleInflationProtectionField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Inflation protection type',
              tooltip:
                'When true, inflation is applied as a flat annual increase rather than compounding.',
              key: 'isSimpleInflationProtection',
              format: 'text',
              getValue: ({ policy }) =>
                policy.isSimpleInflationProtection ? 'Simple' : 'Compound',
            },
          ],
        },
      ],
    ],
    [
      C.InflationProtectionFieldGroup,
      [
        {
          kind: 'fields',
          fields: [
            { label: 'Inflation protection', key: 'policyInflationProtection', format: 'percent' },
            {
              label: 'Inflation protection type',
              key: 'isSimpleInflationProtection',
              format: 'text',
              getValue: ({ policy }) =>
                policy.isSimpleInflationProtection ? 'Simple' : 'Compound',
            },
          ],
        },
      ],
    ],
    [
      C.InflationProtectionYearsField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Inflation protection years',
              key: 'policyInflationProtectionYears',
              format: 'years',
            },
          ],
        },
      ],
    ],
    [
      C.IsIndemnityPolicyPaymentField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Payment type',
              tooltip:
                'Form input flag. Note: a strategy can override this (e.g. CareMatters II is always indemnity).',
              key: 'isIndemnityPolicyPayment',
              format: 'text',
              getValue: ({ policy }) =>
                policy.isIndemnityPolicyPayment ? 'Indemnity' : 'Reimbursement',
            },
          ],
        },
      ],
    ],
    [
      C.IndemnityBenefitPercentField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Indemnity benefit percent',
              tooltip:
                'Percent of the monthly benefit limit paid as cash indemnity. Asset Care fixes this at 75%; a missing or zero value defaults to 75%.',
              key: 'policyIndemnityBenefitPercent',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.PrimaryPolicyHolderField,
      [
        {
          kind: 'fields',
          fields: [
            {
              // The form renders client and partner names from Redux. The
              // policy-only details model can only mirror the selected role.
              label: 'Primary policy holder',
              key: 'isPrimaryPolicyHolder',
              format: 'text',
              getValue: ({ policy }) =>
                policy.isPrimaryPolicyHolder ? 'Primary holder' : 'Partner',
            },
          ],
        },
      ],
    ],
    [
      C.PolicyMonthlyWithdrawalRate,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Monthly withdrawal rate',
              key: 'policyMonthlyWithdrawalRate',
              format: 'percent',
            },
          ],
        },
      ],
    ],
    [
      C.BenefitUtilizedToDateField,
      [
        {
          kind: 'fields',
          fields: [
            {
              label: 'Benefit utilized to date',
              key: 'policyBenefitUtilizedToDate',
              format: 'currency',
            },
          ],
        },
      ],
    ],
  ]);
  return componentDescriptors;
}

export function descriptorsForFormFields(formFields: FC[]): FormFieldGroup[] {
  const descriptors = getComponentDescriptors();
  const result: FormFieldGroup[] = [];
  for (const Fc of formFields) {
    const groups = descriptors.get(Fc);
    if (groups) result.push(...groups);
  }
  return result;
}
